import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import app from "./firebase";
import LoadingComponent from "./LoadingComponent";
import TranslateDropdown, { calcCredits } from "./components/TranslateDropdown";
import { useAuth } from "./context/AuthContext";

export default function YouTubePage() {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [format, setFormat] = useState("qna");
  const [translateTo, setTranslateTo] = useState("");
  const [numQuestions, setNumQuestions] = useState(50);
  const [loading, setLoading] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:5000" : "");
  const navigate = useNavigate();
  const { credits, currentUser, refreshCredits } = useAuth();
  const creditCost = calcCredits("youtube", format, translateTo, numQuestions);

  const handleFormatChange = (e) => setFormat(e.target.value);

  const handleYoutubeProcess = async () => {
    if (!youtubeUrl) return alert("Please enter a YouTube URL!");

    setLoading(true);

    try {
      const auth = getAuth(app);
      const user = auth.currentUser;

      if (!user) {
        alert("Please log in to use this feature");
        setLoading(false);
        return;
      }

      const token = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/api/process-youtube`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          youtube_url: youtubeUrl,
          format,
          language: "en",
          translateTo,
          numQuestions
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        throw new Error(`Server error (${response.status}). The backend may be overloaded or restarting. Please try again in a few seconds.`);
      }

      const data = await response.json();

      if (!response.ok) {
        if (data.error === "DURATION_EXCEEDED") {
          alert("youtube exceeds 15 minutes, more feature will be available once the trial version ends.");
          setLoading(false);
          return;
        }
        const errorMessage = data.error || data.message || "Processing failed";
        throw new Error(errorMessage);
      }

      if (!data.transcript) {
        throw new Error("No transcript received from server");
      }

      if (refreshCredits) refreshCredits();

      navigate("/preview", {
        state: {
          transcript: data.transcript,
          qna_result: data.qna_result || [],
          paragraph_result: data.paragraph_result || "",
          mcq_result: data.mcq_result || [],
          fileName: `YouTube_${new Date().toISOString().split("T")[0]}`,
          format: format,
          youtubeUrl: youtubeUrl,
        },
      });
    } catch (error) {
      console.error("Error:", error);
      alert(`Something went wrong: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ textAlign: "center", marginTop: "50px" }}>
      <h1>📺 YouTube to Content Generator</h1>
      <p style={{ color: "var(--text-muted)", marginBottom: "20px" }}>
        📌 Only <strong>English</strong> YouTube videos are supported.
      </p>

      {!loading ? (
        <div className="glass-container" style={{ maxWidth: "600px", margin: "0 auto" }}>

          {currentUser && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 0, 0, 0.2), rgba(255, 77, 77, 0.2))',
              border: '1px solid #ff0000',
              borderRadius: '10px',
              padding: '15px',
              marginBottom: '20px',
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#fff'
            }}>
              💰 Wallet Balance: {credits !== null ? credits : "—"} Credits
            </div>
          )}

          <div style={{ marginBottom: "20px" }}>
            <label htmlFor="youtube-url">YouTube URL (Max limit is 15 mins):</label>
            <input
              id="youtube-url"
              type="text"
              placeholder="Enter YouTube URL..."
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              disabled={loading}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label>Output Format:</label>
            <div className="radio-group" style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <input type="radio" value="qna" checked={format === "qna"} onChange={handleFormatChange} disabled={loading} />
                Q&A (10 Comprehensive Pairs)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <input type="radio" value="paragraph" checked={format === "paragraph"} onChange={handleFormatChange} />
                Paragraph (Large)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <input type="radio" value="mcq" checked={format === "mcq"} onChange={handleFormatChange} />
                MCQ
              </label>
            </div>
            {format === "mcq" && (
              <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem' }}>
                  Number of Questions: <strong>{numQuestions}</strong>
                </label>
                <input
                  type="range"
                  min="10"
                  max="200"
                  step="10"
                  value={numQuestions}
                  onChange={(e) => setNumQuestions(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--primary-color)' }}
                />
                <p style={{ fontSize: '0.75rem', color: numQuestions > 100 ? 'orange' : 'var(--text-muted)', marginTop: '5px' }}>
                  * Base is 200 credits up to 100 Qs.
                  {numQuestions > 100 && ` Surcharge: +${numQuestions - 100} extra credits.`}
                </p>
              </div>
            )}
          </div>

          {/* Translation temporarily disabled for testing */}


          <button
            className="btn-primary"
            onClick={handleYoutubeProcess}
            disabled={!youtubeUrl}
            style={{ width: "100%", background: "linear-gradient(45deg, #ff0000, #ff4d4d)" }}
          >
            Process YouTube Video — {creditCost} Credits
          </button>
        </div>
      ) : (
        <LoadingComponent />
      )}
    </div>
  );
}